import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Chip,
  Grad,
  StepRail,
  WindowFrame,
  tint,
  type ExplainerDeck,
} from "@/components/suite/SuiteExplainer";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const ACCENT = "#4E9CFF";

/* ─────────────────────────  Mail teaching visuals  ───────────────────────── */

const Avatar = ({ initials, hue }: { initials: string; hue: string }) => (
  <span
    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
    style={{ background: `linear-gradient(135deg, ${hue}, ${tint(hue, 0.7)})` }}
  >
    {initials}
  </span>
);

const MailRow = ({
  initials,
  hue,
  from,
  subject,
  answered,
  needsYou,
}: {
  initials: string;
  hue: string;
  from: string;
  subject: string;
  answered?: boolean;
  needsYou?: boolean;
}) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all",
      needsYou ? "bg-white ring-2" : "bg-white/60",
      answered && "opacity-55",
    )}
    style={needsYou ? ({ "--tw-ring-color": tint(ACCENT, 0.6) } as React.CSSProperties) : undefined}
  >
    <Avatar initials={initials} hue={hue} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-[13px] font-semibold text-[#222B45]">{from}</p>
      <p className="truncate text-xs text-genie-textSecondary">{subject}</p>
    </div>
    {answered && (
      <Chip accent={ACCENT} className="shrink-0">
        <Check className="h-3 w-3" style={{ color: ACCENT }} />
        Answered
      </Chip>
    )}
    {needsYou && (
      <Chip accent={ACCENT} tone="solid" className="shrink-0">
        Needs you
      </Chip>
    )}
  </div>
);

/** The Genie's inbox after a morning of customer email: four answered, one forwarded. */
const InboxMock = () => (
  <WindowFrame title="Your Genie's inbox this morning" accent={ACCENT} className="max-w-md">
    <div className="mb-3 flex items-center justify-between">
      <span className="text-sm font-bold text-[#222B45]">Support</span>
      <span className="text-xs text-genie-textSecondary">1 for you · 4 answered</span>
    </div>
    <div className="space-y-1.5">
      <MailRow initials="RB" hue="#FF4DB8" from="Rachel, Northside Ltd" subject="Re: quote for the extension" needsYou />
      <MailRow initials="TA" hue="#4E9CFF" from="Tom Alder" subject="Are you open on Saturdays?" answered />
      <MailRow initials="PS" hue="#FFBB33" from="Priya S" subject="Where is my order?" answered />
      <MailRow initials="MK" hue="#4E9CFF" from="Mike Kelter" subject="Can I move my booking to Thursday?" answered />
      <MailRow initials="LW" hue="#FF4DB8" from="Lena W" subject="Do you deliver to Hamilton?" answered />
    </div>
  </WindowFrame>
);

/** The mailbox as it appears on the Genie's own page: created with the Genie, nothing to connect. */
const AddressMock = () => (
  <div className="relative mx-auto max-w-md">
    <WindowFrame title="Genie mailbox" accent={ACCENT} float={false}>
      <div className="flex items-center gap-3">
        <span
          className="flex h-11 w-11 items-center justify-center rounded-xl"
          style={{ backgroundColor: tint(ACCENT, 0.14) }}
        >
          <img src="/genie-mascots/role/concierge.png" alt="" className="h-8 w-8 object-contain" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#222B45]">Northside Reception</p>
          <p className="truncate text-xs text-genie-textSecondary">
            <span className="font-semibold text-[#222B45]">northside-reception</span>@ your Genie address
          </p>
        </div>
        <Chip accent={ACCENT} tone="solid" className="ml-auto shrink-0">
          Active
        </Chip>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {[
          { genieIcon: "sync" as const, label: "Forward support@ here", sub: "Keep the address customers know" },
          { genieIcon: "chat-check" as const, label: "Escalates to you", sub: "When confidence is low" },
        ].map((item) => (
          <div key={item.label} className="flex items-start gap-2 rounded-xl border border-[#E5EAF0] bg-white px-3 py-2.5">
            <GenieIcon name={item.genieIcon} size={16} className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-[#222B45]">{item.label}</p>
              <p className="text-[11px] text-genie-textSecondary">{item.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </WindowFrame>
  </div>
);

const InboxZeroMock = () => (
  <WindowFrame title="Your Genie's inbox this morning" accent={ACCENT} className="max-w-md">
    <div className="rounded-xl p-4" style={{ background: tint(ACCENT, 0.06) }}>
      <MailRow initials="RB" hue="#FF4DB8" from="Rachel, Northside Ltd" subject="Re: quote for the extension" needsYou />
      <p className="mt-2 px-3 text-xs text-genie-textSecondary">
        Forwarded with the Genie's draft attached. Send it, or edit and send.
      </p>
    </div>
    <div className="mt-4 flex items-center gap-3 rounded-xl border border-dashed border-[#E5EAF0] px-4 py-3">
      <span
        className="flex h-9 w-9 items-center justify-center rounded-full"
        style={{ backgroundColor: tint(ACCENT, 0.14) }}
      >
        <Check className="h-4 w-4" style={{ color: ACCENT }} />
      </span>
      <p className="text-sm text-genie-textSecondary">
        <span className="font-semibold text-[#222B45]">Everything else is answered.</span> From your
        knowledge base, in your voice, logged.
      </p>
    </div>
  </WindowFrame>
);

/* ────────────────────────────────  Deck  ──────────────────────────────────── */

export const mailDeck: ExplainerDeck = {
  appId: "mail",
  enterLabel: "Enter Help Genie Mail",
  slides: [
    {
      key: "hero",
      eyebrow: "What it is",
      title: (
        <>
          Your support email. <Grad>Taken care of.</Grad>
        </>
      ),
      body: "Every Genie you build comes with its own email address. Customers write in, your Genie answers from the same knowledge base it uses on calls and chat, and only the messages it is not sure about reach you.",
      visual: <InboxMock />,
    },
    {
      key: "how",
      eyebrow: "How it works",
      title: (
        <>
          Answered by your Genie, <Grad>escalated when it matters.</Grad>
        </>
      ),
      body: "Each email is read, answered from your knowledge base, and logged. When confidence drops, the thread is forwarded to you with the Genie's draft and its reasoning, so you are never the bottleneck and never out of the loop.",
      features: [
        { genieIcon: "brain" as const, label: "Grounded in your knowledge base", sub: "Every answer comes from what you have taught your Genie. Nothing invented." },
        { genieIcon: "target" as const, label: "Escalates when unsure", sub: "Forwarded to you with the draft and the reason it held back." },
        { genieIcon: "document" as const, label: "Every reply logged", sub: "The decision, the confidence, the outcome. All visible from the Genie." },
      ],
      visual: (
        <WindowFrame title="Behind the scenes" accent={ACCENT} className="max-w-md">
          <StepRail
            accent={ACCENT}
            steps={[
              { genieIcon: "mail" as const, title: "Mail lands at your Genie's address", sub: "Customers write to it directly, or you forward your support address." },
              { genieIcon: "brain" as const, title: "Your Genie reads and answers", sub: "Grounded in your knowledge base, in your brand voice." },
              { genieIcon: "chat-check" as const, title: "Only the unsure ones reach you", sub: "Forwarded with the draft, ready to send or edit." },
            ]}
          />
        </WindowFrame>
      ),
    },
    {
      key: "address",
      eyebrow: "One address per Genie",
      title: (
        <>
          No accounts to connect. <Grad>Nothing to set up.</Grad>
        </>
      ),
      body: "The address is created the moment your Genie is. Put it on your website, share it, or forward your existing support address to it. Rename it, pause it, or run it in test mode from the Genie's own page.",
      features: [
        { genieIcon: "mail" as const, label: "Created with the Genie", sub: "Live from day one, managed from My Genies." },
        { genieIcon: "sync" as const, label: "Forward what you already have", sub: "Point support@ at it and keep the address customers know." },
      ],
      visual: <AddressMock />,
    },
    {
      key: "payoff",
      eyebrow: "The payoff",
      title: (
        <>
          Only the mail that <Grad>actually needs you</Grad>.
        </>
      ),
      body: "Your inbox stops being a queue. Routine questions are answered in minutes, around the clock, and what reaches you is a short list with a draft already attached. That is the whole idea. We are signing you in now.",
      visual: <InboxZeroMock />,
    },
  ],
};
